<script>
import { defineComponent, markRaw } from 'vue'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridAccountsPayableColumns from '@/Mixins/SigmaClientNew/AgGridAccountsPayableColumns'
import AgGridDDColumns from '@/Mixins/SigmaClientNew/AgGridDDColumns'
import { AgGridVue } from 'ag-grid-vue3'

export default defineComponent({
  name: 'PayrollDirectDepositList',

  components: {
    AgGridVue
  },
  mixins: [ AgGridMethods, AgGridAccountsPayableColumns, AgGridDDColumns ],

  props: {
    // Name of input field.  Not really necessary, but helpful if we use the form object down the road
    name: {
      required: true,
      type: String
    },

    iteration: {
      required: true,
      type: Number
    },

    width: {
      required: true,
      type: String
    },

    height: {
      required: true,
      type: String
    },

    classes: {
      required: false,
      type: String,
      default: 'ag-theme-balham'
    },

    rowData: {
      required: false
    },

    // Ag-grid column defs
    columns: {
      required: false,
      type: Array,
      default: undefined
    },

    // Ag-grid Options
    options: {
      required: false,
      type: Object,
      default: undefined
    }
  },

  data () {
    return {
      isError: false, // If the input or selected field is incorrect, show an error
      isLoading: false,
      scope: markRaw(this),
      gridApi: null,
      columnApi: null,
      defaultColDefs: undefined,
      defaultGridOptions: undefined,
      changes: []
    }
  },

  computed: {
    columnDefs () {
      if (this.columns && Array.isArray(this.columns)) {
        return this.columns
      } else {
        return this.defaultColDefs
      }
    },
    gridOptions () {
      return this.options || this.defaultGridOptions
    },

    // Any groups with at least one transaction selected
    selectedGroups () {
      let aggregatedNodes = []
      this.gridApi.forEachNode((node) => {
        if (node.aggData) {
          const selected = node.allLeafChildren.find((child) => child.selected)
          if (selected) aggregatedNodes.push(node)
        }
      })
      return aggregatedNodes
    },

    // Since we are now sending invoices grouped by invoice, this helper makes each group look like a transaction
    simulatedTransactionData () {
      if (!this.selectedGroups.length) return []

      let transactions = []
      this.selectedGroups.forEach((group) => {
        const data = group.aggData
        const transaction = {
          attributes: {
            vendor: data.vendorResource.id,
            vendor_name: data.vendorResource.attributes.name,
            routing: data.vendorResource.attributes.ach_routing_number,
            account: data.vendorResource.attributes.ach_account_number,
            amount_decimal: data.selected_amount_decimal,
            description: group.allLeafChildren ? group.allLeafChildren.map((child) => child.data.attributes.number).join(', ') : '',
            payment_confirmation_number: '',
            resources: group.allLeafChildren ? group.allLeafChildren.filter((child) => child.selected).map((child) => child.data) : null
          }
        }
        transactions.push(transaction)
      })

      return transactions
    }
  },

  methods: {
    hasCompleteAchData (vendorResource) {
      if (!vendorResource || !vendorResource.attributes) return false
      const routing = vendorResource.get('ach_routing_number')
      const account = vendorResource.get('ach_account_number')
      return (routing && account)
    },

    updateTotals (gridApi) {
      const api = gridApi || this.gridApi
      if (!api) return

      let sum = 0
      let selected = 0
      api.forEachNode((row) => {
        if (row && row.data && row.data.attributes) {
          sum += parseFloat(row.data.attributes.amount_decimal || '0')
          if (row.selected) {
            selected += parseFloat(row.data.attributes.amount_decimal || '0')
          }
        }
      })
      this.$emit('update-totals', {
        totalAvailable: sum.toFixed(2),
        totalSelected: selected.toFixed(2)
      })
    },

    // For this, the real node/row is selectable if the routing and bank account exists on the vendor
    isRowSelectable (params) {
      return false
      /*
      if (params.data && this.hasCompleteAchData(params.data.get('vendor'))) {
        return true
      } else if (params.node && params.node.allChildrenCount) {
        return params.node.allLeafChildren.every((child) => {
          return this.hasCompleteAchData(child.data.get('vendor'))
        })
      } else {
        return false
      }
      */
    },

    // Ag-grid
    onGridReady (params) {
      if (!params) return
      this.gridApi = params.api
      this.columnApi = params.api
    },

    onFirstDataRendered (params) {
      if (!params) return
      this.safeSizeColumnsToFit(params)
      this.updateTotals(params.api)
    },

    onCellValueChanged (params) {
      // Ignore if not changed
      if ((params.value && params.oldValue === params.newValue) || !params.data) return
    },

    onChangeVisibleColumns () {
      this.$nextTick(() => {
        this.gridApi.sizeColumnsToFit()
      })
    },

    exportToExcel () {
      this.gridApi.exportDataAsExcel()
    }
  },
  beforeMount () {
    this.defaultColDefs = [
      { headerName: 'Project', field: 'project_name', width: 200 },
      { headerName: 'PR #', field: 'payroll_number', width: 120 },
      { headerName: 'PR ID', field: 'payroll_id', width: 50 },
      { headerName: 'AP ID', field: 'ap_id', width: 50 },
      { headerName: 'Time Posted', field: 'time_posted', width: 100 },
      { headerName: 'A ID', field: 'assignment_id', width: 50 },
      { headerName: 'Emp ID', field: 'employee_id', width: 50 },
      { headerName: 'Check Date', field: 'check_date', width: 100 },
      // { headerName: 'Identifier', field: 'identifer', width: 150 },
      this.ddAmountColumn,
      { headerName: 'Payee', field: 'payee', width: 150 },
      // { headerName: 'DD Name', field: 'dd_name', width: 150 },
      { headerName: 'DD Account', field: 'account_number', width: 150, cellClass: 'textFormat' },
      { headerName: 'DD Routing', field: 'routing_number', width: 100, cellClass: 'textFormat' },
      this.ddIsSavingsColumn
    ]

    this.defaultGridOptions = {
      singleClickEdit: false,
      undoRedoCellEditing: false,
      enterNavigatesVertically: false,
      enterNavigatesVerticallyAfterEdit: false,
      rowSelection: 'multiple',
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: [],
        suppressSizeToFit: false
      },
      pinnedBottomRowData: [],
      enableRangeSelection: true,
      animateRows: true,

      // Grouping related
      suppressAggFuncInHeader: true,
      // groupUseEntireRow: true,
      groupSelectsChildren: true,
      // groupIncludeTotalFooter: true,
      autoGroupColumnDef: {
        headerName: 'Vendor',
        minWidth: 200,
        cellRendererParams: {
          suppressCount: true
        }
      },
      aggFuncs: {
        sum: this.customSum, // this overrides the grids built in sum function
        sumSelected: this.customSumSelected
      },

      // Classes and events
      rowClassRules: {
        'ag-error': (params) => params.node.aggData && !this.hasCompleteAchData(params.node.aggData.vendorResource)
      },

      excelStyles: [
        {
          id: 'amount',
          numberFormat: {
            format: '#,##0.00'
          }
        },
        {
          id: 'textFormat',
          dataType: 'string'
        }
      ],
      // isRowSelectable: (params) => this.isRowSelectable(params),
      onGridReady: (params) => this.onGridReady(params),
      onFirstDataRendered: (params) => this.onFirstDataRendered(params),
      onCellValueChanged: (params) => this.onCellValueChanged(params),
      onSelectionChanged: (params) => {
        this.updateTotals(params.api)
        this.gridApi.refreshClientSideRowModel('aggregate')
      }
    }
  }
})
</script>

<template>
<div>
  <ag-grid-vue
    :name="name"
    :ref="name"
    :class="classes"
    :style="{ width, height }"
    :key="iteration"
    :column-defs="columnDefs"
    :row-data="rowData"
    :grid-options="gridOptions"
  />
</div>
</template>
